import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSelector, useDispatch } from 'react-redux';
import { Row, Modal } from 'react-bootstrap';
import ReactMarkdown from 'react-markdown';
import dayjs from 'dayjs';
import { IoMdCopy } from 'react-icons/io';
import { AiOutlineFileWord } from 'react-icons/ai';
import { MdRefresh } from 'react-icons/md';

import { docxExport } from './DisasterGPTDataHelpers/DGPTExport';

import DataSources from 'components/DisasterGPT/DataSources';
import AgentModeSelector from 'components/DisasterGPT/AgentModeSelector';
import StylishNewTextArea from 'components/DesignSystems/New/StylishNewTextArea';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import AnnotationRenderer from './AnnotationRenderer';
import SlashCommandMenu from './SlashCommandMenu';
import ScenarioExamplesModal from './ScenarioExamplesModal';
import CompactionDivider from './CompactionDivider';
import ContextMeter from './ContextMeter';
import {
  exportableMessages,
  findAnsweredUserIndex,
  isCompactionMarker,
} from './chatTypes';
import { useAICatalog } from './hooks/useAICatalog';
import { useFetchCitationFileUrl } from './hooks/useFetchCitationFileUrl';
import { useAICommands } from './hooks/useAICommands';

import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';

import {
  setSelectedDChat,
  setCurrentSitrepId,
  cancelRun,
  resumeDChat,
  compactDChat,
} from 'slices/dchatSlice';
import ReviewQueueModal from './agentConfirm/ReviewQueueModal';
import { icsAllQueryKeyBases, invalidateIcsQueries } from './icsInvalidation';

import {
  usePollDChat,
  useStopPollDChat,
  usePostDChat,
  useArchiveDisasterChat,
} from './hooks/useDisasterChats';
import {
  selectIncident,
  selectUserGuid,
  useAppSelector,
} from 'slices/commonSelectors';

import './SitrepSection.css';

/**
 * Status labels shown while a turn runs, where the raw COMS status reads
 * poorly.
 */
const STATUS_LABELS = { Compacting: 'Summarizing earlier conversation' };

/**
 * Expand a bare slash-command token (e.g. "/review-objectives") into its full
 * "<command> <prompt>" form before sending. The command token deliberately
 * stays part of the sent prompt — users should see and get comfortable with the
 * syntax, never have it swapped out for friendly language. Input that already
 * has text after the token (the normal case: the review buttons and the "/"
 * palette both prefill "<command> <prompt>" for editing) is sent exactly as
 * written. Ordinary text starting with "/" is returned unchanged.
 */
export const expandSlashCommand = (text, commands) => {
  if (!text.startsWith('/') || !Array.isArray(commands)) {
    return text;
  }
  for (const cmd of commands) {
    if (text.trim() === cmd.command) {
      return `${cmd.command} ${cmd.promptTemplate}`;
    }
  }
  return text;
};

const ChatDisasterGPT = ({ toggle, selectedDatetime }) => {
  const reduxDispatch = useDispatch();

  const [input, setInput] = useState('');
  const [slashMenuOpen, setSlashMenuOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [sendMessageTimestamp, setSendMessageTimestamp] = useState();
  const [realtimeSelections, setRealtimeSelections] = useState([]);
  const [priorStatus, setPriorStatus] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedMode, setSelectedMode] = useState('agent_confirm');
  const incident = useAppSelector(selectIncident);

  const dchatStatus = useSelector((state) => state.dchat.status);
  const dchat = useSelector((state) => state.dchat.dchat);
  const streamtext = useSelector((state) => state.dchat.streamtext);

  // Single source of truth for "the agent is actively working". Used for the
  // input/progress swap, the disabled states, and the Cancel button so they can
  // never disagree and visibly thrash as the polled status churns.
  const isAgentRunning = dchatStatus === 'loading' || dchatStatus === 'ongoing';

  // Chats owned by another workspace member are viewable but not actionable:
  // no sending, cancelling, archiving, or HITL decisions. The server enforces
  // ownership on every mutation — this only keeps the affordances honest.
  const myGuid = useAppSelector(selectUserGuid);
  const isReadOnly =
    !!dchat?.user_guid && !!myGuid && dchat.user_guid !== myGuid;
  const ownerLabel = dchat?.owner_name || dchat?.owner_email || 'the owner';

  const pollDChat = usePollDChat();
  const stopPollDChat = useStopPollDChat();
  const postDChat = usePostDChat();
  const archiveDisasterChat = useArchiveDisasterChat();
  const handleFetchFileUrl = useFetchCitationFileUrl();
  const { data: aiCatalog } = useAICatalog();
  const [compacting, setCompacting] = useState(false);

  async function compactNowClicked() {
    setCompacting(true);
    await reduxDispatch(compactDChat(dchat.id));
    setCompacting(false);
  }

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Slash-command palette source. The palette (SlashCommandMenu) owns its own
  // search box, collapsible categories, and keyboard navigation — here we just
  // supply the full curated command list and the open/close + select wiring.
  // Picking a command prefills an editable prompt; it does not invoke a tool
  // directly — the agent still runs normally. Mode-aware: guided skills are
  // hidden in read-only 'ask' mode (the backend skips the skill layer there).
  const allCommands = useAICommands(selectedMode);

  // Derive SharePoint and tenant values from incident prop
  const sharepointDriveId = useMemo(
    () => incident?.sharepoint_location,
    [incident]
  );
  const tenantId = useMemo(() => incident?.tenant_id, [incident]);

  // Simplified sendMessage function
  const sendMessage = useCallback(
    (message, newChatName = '', prePromptData = null) => {
      const data = prePromptData !== null ? prePromptData : realtimeSelections;

      let finalNewChatName = newChatName;
      if (!newChatName || !newChatName.length) {
        // Chats launched from an AIReviewButton carry a pre-assigned name
        // (e.g. "Review Objectives"); otherwise fall back to the message text.
        finalNewChatName = (!dchat.id && dchat.name) || message.slice(0, 50);
      }

      postDChat({
        dchatId: dchat.id,
        message: message,
        realtimeSelections: data,
        selectedDatetime: selectedDatetime,
        newChatName: finalNewChatName,
        tenantId: tenantId,
        sharepointDriveId: sharepointDriveId,
        messages: messages,
        mode: selectedMode,
      });

      setMessages([
        ...messages,
        {
          role: 'USER',
          content: message,
          timestamp: new Date(),
          realtimeSelections: data,
          selectedDatetime: selectedDatetime,
        },
      ]);
      setInput('');
      setSendMessageTimestamp(new Date());
    },
    [
      realtimeSelections,
      dchat.id,
      dchat.name,
      selectedDatetime,
      tenantId,
      sharepointDriveId,
      messages,
      postDChat,
      setMessages,
      setInput,
      setSendMessageTimestamp,
      selectedMode,
    ]
  );

  const areSelectionsValid = useCallback(() => {
    if (realtimeSelections.length) {
      const sitrepSelection = realtimeSelections.find(
        (s) => s.source === 'SITREP'
      );
      if (!!sitrepSelection && !sitrepSelection.id) {
        return false;
      }
    }
    return true;
  }, [realtimeSelections]);

  // Trigger button: open/close the command palette.
  const toggleCommandMenu = useCallback(() => {
    setSlashMenuOpen((open) => !open);
  }, []);

  const closeCommandMenu = useCallback(() => {
    setSlashMenuOpen(false);
    inputRef.current?.focus();
  }, []);

  // Insert editable text into the input and refocus with the cursor at the
  // end. Shared by the palette select and the examples view's "Use this prompt".
  const insertIntoInput = useCallback((text) => {
    setInput(text);
    setSlashMenuOpen(false);
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (el) {
        el.focus();
        const end = el.value.length;
        el.setSelectionRange(end, end);
      }
    });
  }, []);

  const selectSlashCommand = useCallback(
    (cmd) => {
      // Row click inserts just the bare command token — quick and minimal; the
      // user can add their own words after it. Sending a bare token expands it
      // to "<command> <prompt>" (expandSlashCommand).
      insertIntoInput(`${cmd.command} `);
    },
    [insertIntoInput]
  );

  // The row's "Use prompt" action: prefill the full "<command> <example
  // prompt>" for editing. Nothing is sent automatically.
  const useCommandPrompt = useCallback(
    (cmd) => {
      insertIntoInput(`${cmd.command} ${cmd.promptTemplate}`);
    },
    [insertIntoInput]
  );

  // "Learn more" on a palette row: swap the palette for the command's info
  // modal (data-source pills, the exact prompt, and — for registry-backed
  // commands — guidance and the expected responses users/QA compare their
  // real output against).
  const [examplesCommand, setExamplesCommand] = useState(null);
  const viewCommandExamples = useCallback((cmd) => {
    setSlashMenuOpen(false);
    setExamplesCommand(cmd);
  }, []);

  const useModalPrompt = useCallback(
    (cmd) => {
      setExamplesCommand(null);
      insertIntoInput(`${cmd.command} ${cmd.promptTemplate}`);
    },
    [insertIntoInput]
  );

  // Expand a leading slash-command literal back to its prompt template before sending, so the
  // user sees a clean command but the agent gets a well-formed instruction. Any text the user
  // typed after the command is preserved. Non-command input is sent unchanged.
  const submitMessage = useCallback(() => {
    // No new turn while "Compact now" rewrites the model context (PRAT-4843).
    if (compacting || !areSelectionsValid() || input.length === 0) {
      return;
    }
    sendMessage(expandSlashCommand(input, allCommands));
  }, [compacting, input, allCommands, sendMessage, areSelectionsValid]);

  const handleInputKeyDown = useCallback(
    (e) => {
      // Typing "/" as the first character opens the command palette instead of
      // inserting a slash (the palette then owns search + selection). The "/"
      // button does the same thing via a click.
      if (e.key === '/' && input.length === 0) {
        e.preventDefault();
        setSlashMenuOpen(true);
        return;
      }

      // Default behavior: Enter sends (Shift+Enter inserts a newline).
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        submitMessage();
      }
    },
    [input, submitMessage]
  );

  // Keep the mode selector in sync with the loaded chat's persisted mode, so
  // reopening a chat (or reloading the page) doesn't silently show the default.
  useEffect(() => {
    if (dchat?.id && dchat.mode) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSelectedMode(dchat.mode);
    }
  }, [dchat?.id, dchat?.mode]);

  // Prefill the input with the full canned prompt when a new chat is launched
  // from an AIReviewButton ("Review X"). Nothing is sent — the prompt sits
  // editable in the textarea so the user can tailor it and press Send
  // themselves. Applies the scenario's mode (agent_confirm for reviews, so
  // proposed writes always pause for HITL confirmation) regardless of the
  // previously selected mode. The ref keys on the launch object identity so
  // each button click prefills exactly once, even if the user cleared the
  // input.
  const handledInitialInputRef = useRef(null);
  useEffect(() => {
    if (
      dchat &&
      dchat.initialInput &&
      !dchat.id &&
      handledInitialInputRef.current !== dchat
    ) {
      handledInitialInputRef.current = dchat;
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSelectedMode(dchat.mode ?? 'agent_confirm');
      setInput(dchat.initialInput);
      inputRef.current?.focus();
    }
  }, [dchat]);

  useEffect(() => {
    // Only poll if there's an active chat that needs polling
    // This handles cases like page refresh where a chat is still processing
    // For new chats, polling starts automatically when a message is sent via postDChat
    if (dchat?.id && (dchatStatus === 'loading' || dchatStatus === 'ongoing')) {
      pollDChat(dchat.id);
    }

    // Cleanup: stop polling when component unmounts
    return () => {
      stopPollDChat();
    };
  }, [dchat?.id, dchatStatus, pollDChat, stopPollDChat]);

  // Track previous status to display during loading states
  // This intentionally reads and updates priorStatus to track state transitions
  useEffect(() => {
    if (dchat) {
      if (dchat.status !== 'Complete' && dchat.status !== 'Error') {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setPriorStatus(dchat.status);
      } else if (
        (dchat.status === 'Complete' && priorStatus !== 'Complete') ||
        (dchat.status === 'Error' && priorStatus !== 'Error')
      ) {
        setPriorStatus('Initializing');
      } else {
        setPriorStatus(dchat.status);
      }
    }
  }, [dchat, priorStatus]);

  // Synchronize Redux dchat state with local component state
  // This is intentional: local state enables optimistic updates in sendMessage/handleRedoMessage
  // while this effect syncs with server state when dchat updates
  useEffect(() => {
    if (dchat?.messages) {
      let showMsg = [...dchat.messages];
      // Keep the agent's streamed narration visible while a leg is in progress AND while the approval modal is up
      // (pending_confirmation) — otherwise the narration flickers away each time the agent pauses for approval.
      if (
        streamtext &&
        (dchatStatus === 'loading' ||
          dchatStatus === 'ongoing' ||
          dchatStatus === 'pending_confirmation')
      ) {
        const streamMsg = {
          role: 'ASSISTANT',
          content: streamtext,
        };
        showMsg = [...showMsg, streamMsg];
      }
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setMessages(showMsg);
    }
    if (!dchat || Object.keys(dchat).length === 0) {
      setMessages([]);
    }

    // Restore the data sources from the latest real message: a "Compact now"
    // divider can be the last entry and carries no selections.
    const lastMessage = dchat?.messages?.findLast(
      (message) => !isCompactionMarker(message)
    );
    if (lastMessage) {
      setRealtimeSelections(lastMessage.realtimeSelections || []);
    }
  }, [dchat, streamtext, dchatStatus]);

  useEffect(() => {
    if (messagesEndRef.current && dchatStatus === 'succeeded') {
      messagesEndRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
      });
    }
  }, [dchatStatus]);

  // Belt-and-braces ICS refresh when an incident-scoped agent run completes:
  // the per-write dchat_ics_update websocket frames handle the common case
  // (DgptStreamProvider), but frames have no delivery guarantee (reconnect cap,
  // incident-id gate) — sweep the ICS query bases once on completion so no
  // form the agent wrote stays stale. invalidateQueries only refetches
  // queries that are currently mounted, so the sweep is cheap.
  const queryClient = useQueryClient();
  const prevDchatStatusRef = useRef(dchatStatus);
  useEffect(() => {
    const prev = prevDchatStatusRef.current;
    prevDchatStatusRef.current = dchatStatus;
    if (
      dchatStatus === 'succeeded' &&
      prev !== 'succeeded' &&
      dchat?.incident_id
    ) {
      invalidateIcsQueries(queryClient, icsAllQueryKeyBases);
    }
  }, [dchatStatus, dchat?.incident_id, queryClient]);

  function cancelRunClicked() {
    reduxDispatch(cancelRun(dchat));
  }

  // HITL: the agent is paused on one or more proposed writes awaiting the
  // user's approve/edit/reject decision (see AgentConfirmationModal).
  const pendingConfirmations =
    dchat?.status === 'PendingConfirmation' &&
    Array.isArray(dchat?.pending_confirmation)
      ? dchat.pending_confirmation
      : [];

  // The agent's "ask" for this batch is its streamed narration (the same text shown live in the chat). Surface it in
  // the modal too so the chat and the approval surface present the identical ask.
  const confirmationAskText =
    pendingConfirmations.length > 0 && streamtext ? streamtext : undefined;

  // Returns false when the resume request fails so ReviewQueueModal can leave
  // its "applying" spinner and let the reviewer retry.
  function handleConfirmationSubmit(decisions) {
    return reduxDispatch(resumeDChat(dchat.id, decisions));
  }

  function deleteSessionClicked() {
    if (dchat.id) {
      setShowDeleteModal(true);
    }
  }

  function closeClicked() {
    reduxDispatch(setSelectedDChat({}));
    reduxDispatch(setCurrentSitrepId());
    toggle();
  }

  function handleConfirmDelete() {
    // "Delete" archives (PNG-537): the chat leaves the list and can be restored
    // from the owner's Archived view.
    archiveDisasterChat.mutate(dchat.id);
    closeClicked();
    setShowDeleteModal(false);
  }

  function handleCancelDelete() {
    setShowDeleteModal(false);
  }

  const onUseBlueprint = useCallback(
    (blueprint) => {
      const structuredBlueprint = `# ${blueprint.title}\n\n${blueprint.description}`;
      let promptData = realtimeSelections;

      // Set realtimeSelections to the blueprint's datasources
      if (blueprint.settings?.datasources) {
        setRealtimeSelections(blueprint.settings.datasources);
        promptData = blueprint.settings.datasources;
      }

      // Send message with updated data source selections
      sendMessage(structuredBlueprint, blueprint.title, promptData);
    },
    [realtimeSelections, sendMessage]
  );

  // Auto-send initial blueprint message when a new chat with a blueprint is created
  // This is an intentional side effect that triggers on mount/dchat change
  useEffect(() => {
    if (dchat && dchat.initialBlueprint && !dchat.id && !messages.length) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      onUseBlueprint(dchat.initialBlueprint);
    }
  }, [dchat, messages, onUseBlueprint]);

  const markdownComponents = {
    table({ ...props }) {
      return <table className="custom-markdown-table" {...props} />;
    },
    th({ ...props }) {
      return <th className="custom-markdown-th" {...props} />;
    },
    td({ ...props }) {
      return <td className="custom-markdown-td" {...props} />;
    },
    tr({ ...props }) {
      return <tr className="custom-markdown-tr" {...props} />;
    },
  };

  const customSchema = {
    ...defaultSchema,
    attributes: {
      ...defaultSchema.attributes,
      span: [...(defaultSchema.attributes.span || []), 'style'],
    },
  };

  const handleRedoMessage = (messageIndex) => {
    const userIndex = findAnsweredUserIndex(messages, messageIndex);
    if (userIndex < 0) {
      return;
    }
    const userMessage = messages[userIndex];

    const newMessages = messages.slice(0, userIndex);
    setMessages(newMessages);

    postDChat({
      dchatId: dchat.id,
      message: userMessage.content,
      realtimeSelections: userMessage.realtimeSelections,
      selectedDatetime: selectedDatetime,
      newChatName: '',
      tenantId: tenantId,
      sharepointDriveId: sharepointDriveId,
      messages: newMessages,
      mode: selectedMode,
    });

    setMessages([
      ...newMessages,
      {
        role: 'USER',
        content: userMessage.content,
        timestamp: new Date(),
        realtimeSelections: userMessage.realtimeSelections,
        selectedDatetime: selectedDatetime,
      },
    ]);
    setSendMessageTimestamp(new Date());
  };

  return (
    <div
      className="d-flex flex-column"
      style={{ height: '100%', maxHeight: '100%' }}
    >
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          scrollbarGutter: 'stable',
          padding: '1rem',
          minHeight: 0,
        }}
      >
        <label>
          PRATUS AI generated content may be incorrect. Always check important
          information. For bug reports and feature requests, please contact{' '}
          <a href="mailto:support@disastertech.com">support@disastertech.com</a>
        </label>
        <hr />
        <div>
          {messages.map((message, index) => {
            if (isCompactionMarker(message)) {
              return (
                <CompactionDivider
                  key={'dpgt-compaction-' + message.compaction_id}
                  marker={message}
                />
              );
            }
            let timestamp;

            if (dchat?.id) {
              timestamp = message.timestamp;
            } else {
              timestamp = sendMessageTimestamp;
            }
            if (timestamp) {
              timestamp = dayjs(timestamp).format('YYYY-MM-DD HH:mm');
            } else {
              timestamp = '(No datetime provided)';
            }

            const messageContent = message?.content.replace(
              /\*\*\[[^\]]*\]\*\*|【[^】]*】/g,
              ''
            );

            const messageRole = message.role.toUpperCase();
            const displayRole =
              messageRole === 'ASSISTANT' ? 'PRATUS AI' : messageRole;

            return (
              <div key={'dpgt-message-' + index} id={'dpgt-message-' + index}>
                <div className="d-flex align-items-center gap-2">
                  <strong>{displayRole}:</strong>
                  {messageRole === 'ASSISTANT' && !isReadOnly && (
                    <MdRefresh
                      className="redo-icon"
                      onClick={() => handleRedoMessage(index)}
                      title="Redo this response"
                      style={{ cursor: 'pointer' }}
                    />
                  )}
                  <IoMdCopy
                    className="clipboard-icon"
                    onClick={() =>
                      docxExport(
                        messageContent,
                        'Clipboard',
                        dchat.name || 'PRATUS AI Agent'
                      )
                    }
                    title="Copy to clipboard"
                    style={{ cursor: 'pointer' }}
                  />
                  <AiOutlineFileWord
                    className="download-icon"
                    onClick={() =>
                      docxExport(
                        [message],
                        'Download',
                        dchat.name || 'PRATUS AI Agent'
                      )
                    }
                    title="Click to Download"
                    style={{ cursor: 'pointer' }}
                  />
                </div>
                <div>
                  <label>{timestamp}</label>
                  <div className="EventAIAnalysis-paragraph">
                    {messageRole === 'ASSISTANT' &&
                    message.annotations?.length > 0 ? (
                      <AnnotationRenderer
                        content={messageContent}
                        annotations={message.annotations}
                        onFetchFileUrl={handleFetchFileUrl}
                        messageIndex={index}
                      />
                    ) : (
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        rehypePlugins={[
                          [rehypeRaw, { passThrough: ['element'] }],
                          [rehypeSanitize, customSchema],
                        ]}
                        components={{
                          ...markdownComponents,
                          a: ({ _, ...linkProps }) => {
                            return (
                              <a
                                {...linkProps}
                                target="_blank"
                                rel="noopener noreferrer"
                              />
                            );
                          },
                        }}
                      >
                        {messageContent}
                      </ReactMarkdown>
                    )}
                  </div>
                </div>
                <hr />
              </div>
            );
          })}
        </div>
        <div ref={messagesEndRef} />
      </div>

      {!!dchat?.id &&
        typeof dchat.context_tokens === 'number' &&
        !!aiCatalog?.model && (
          <ContextMeter
            contextTokens={dchat.context_tokens}
            model={aiCatalog.model}
            compactionCount={dchat.compaction_count ?? 0}
            canAct={!isReadOnly}
            busy={isAgentRunning}
            compacting={compacting}
            onCompact={compactNowClicked}
          />
        )}

      <div className="ChatDisasterGPT-input-area">
        <Row>
          {isReadOnly ? (
            <div className="ChatDisasterGPT-input-wrap position-relative">
              <div
                className="text-muted small d-flex align-items-center gap-2 p-2"
                data-testid="chat-readonly-banner"
              >
                <SharedIcon iconName={'visibility'} size={'sm'} />
                <span>
                  Viewing {ownerLabel}&apos;s chat (read-only). Only{' '}
                  {ownerLabel} can send messages or act here.
                </span>
              </div>
            </div>
          ) : isAgentRunning ? (
            <div>
              <h5 className="mb-1">
                {STATUS_LABELS[priorStatus] ?? priorStatus}&nbsp;
                <i className="fa fa-spinner fa-pulse"></i>
              </h5>
              <div>
                <span>
                  PRATUS AI Agent on average is 10-60 seconds but depending on
                  the volume of data can take longer to generate an answer.
                </span>
              </div>
            </div>
          ) : (
            <div className="ChatDisasterGPT-input-wrap position-relative">
              {slashMenuOpen && allCommands.length > 0 && (
                <SlashCommandMenu
                  commands={allCommands}
                  onSelect={selectSlashCommand}
                  onClose={closeCommandMenu}
                  onUsePrompt={useCommandPrompt}
                  onViewExamples={viewCommandExamples}
                />
              )}
              {examplesCommand && (
                <ScenarioExamplesModal
                  command={examplesCommand}
                  onClose={() => setExamplesCommand(null)}
                  onUsePrompt={useModalPrompt}
                />
              )}
              <StylishNewTextArea
                ref={inputRef}
                type="text"
                value={input}
                disabled={isAgentRunning || compacting}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleInputKeyDown}
                placeholder={
                  'Message PRATUS AI Agent...  (type / for commands)'
                }
              />
              <div>
                <div className="ChatDisasterGPT-input-button-wrap">
                  <div className="d-flex justify-content-between align-items-center ChatDisasterGPT-pill-row">
                    <div className="d-flex align-items-center gap-2">
                      <StylishNewButton
                        id="dgpt-command-trigger"
                        className="button--ai button--ai-glow button--icon"
                        onClick={toggleCommandMenu}
                        disabled={isAgentRunning}
                        title="Browse commands"
                      >
                        <span
                          style={{
                            fontWeight: 600,
                            fontSize: '1.1rem',
                            lineHeight: 1,
                          }}
                        >
                          /
                        </span>
                      </StylishNewButton>
                      <DataSources
                        selections={realtimeSelections}
                        setSelections={setRealtimeSelections}
                      />
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <AgentModeSelector
                        selectedMode={selectedMode}
                        onModeChange={setSelectedMode}
                        disabled={isAgentRunning}
                      />
                      <StylishNewButton
                        onClick={submitMessage}
                        disabled={
                          compacting ||
                          !areSelectionsValid() ||
                          input.length === 0
                        }
                        className="button--primary"
                      >
                        Send
                      </StylishNewButton>
                    </div>
                  </div>
                </div>
                {!areSelectionsValid() && (
                  <span>
                    Choose a PRATUS AI SITREP to send to PRATUS AI Agent
                  </span>
                )}
              </div>
            </div>
          )}
        </Row>
      </div>

      <div className="border-top position-relative p-3">
        {/* Cancel lives in its own absolutely-positioned slot so showing/hiding
            it never shifts or re-centers the export icon row. */}
        {isAgentRunning && !!dchat && !isReadOnly && (
          <div
            className="position-absolute"
            style={{ left: '1rem', top: '50%', transform: 'translateY(-50%)' }}
          >
            <StylishNewButton
              className="button--secondary button--icon"
              type="button"
              onClick={cancelRunClicked}
              title="Cancel Current Run"
            >
              <SharedIcon iconName={'cancel'} size={'sm'} />
            </StylishNewButton>
          </div>
        )}

        <div className="d-flex justify-content-center gap-3">
          {!!dchat && !!dchat.id && (
            <>
              <StylishNewButton
                className="button--secondary button--icon"
                type="button"
                onClick={() =>
                  docxExport(
                    exportableMessages(messages),
                    'Download',
                    dchat.name || 'PRATUS AI Agent'
                  )
                }
                title="Download Docx"
              >
                <SharedIcon iconName={'download'} size={'sm'} />
              </StylishNewButton>

              <StylishNewButton
                className="button--secondary button--icon"
                type="button"
                onClick={() =>
                  docxExport(
                    exportableMessages(messages),
                    'Clipboard',
                    dchat.name || 'PRATUS AI Agent'
                  )
                }
                title="Copy to Clipboard"
              >
                <SharedIcon iconName={'content_copy'} size={'sm'} />
              </StylishNewButton>

              {!isReadOnly && (
                <StylishNewButton
                  className="button--secondary button--icon"
                  type="button"
                  onClick={deleteSessionClicked}
                  title="Archive Session"
                >
                  <SharedIcon iconName={'archive'} size={'sm'} />
                </StylishNewButton>
              )}
            </>
          )}
        </div>
      </div>

      <ReviewQueueModal
        show={pendingConfirmations.length > 0}
        confirmations={pendingConfirmations}
        askText={confirmationAskText}
        submitting={dchatStatus === 'loading'}
        onSubmit={handleConfirmationSubmit}
        onClose={toggle}
        readOnly={isReadOnly}
        ownerName={isReadOnly ? ownerLabel : undefined}
      />

      <Modal
        show={showDeleteModal}
        onHide={handleCancelDelete}
        centered
        backdrop={true}
        keyboard={true}
      >
        <Modal.Header closeButton closeVariant="white">
          <Modal.Title>Archive Session</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Archive this PRATUS AI Agent session? It will be hidden from your chat
          list; you can restore it from Archived.
        </Modal.Body>
        <Modal.Footer>
          <StylishNewButton
            className="button--secondary"
            onClick={handleCancelDelete}
          >
            Cancel
          </StylishNewButton>
          <StylishNewButton
            className="button--tertiary"
            onClick={handleConfirmDelete}
          >
            Archive
          </StylishNewButton>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ChatDisasterGPT;
